<template>
  <div id="package-details" class="w-full flex flex-col gap-5">
    <div class="py-6 px-6 flex gap-4 bg-pure-white rounded-2xl">
      <div class="w-full flex flex-col gap-1">
        <BlTextBody2 text="Total items:" class="text-slate-700" />
        <div class="font-bold text-2xl">
          {{ creditPackage.number_of_items }}
        </div>
      </div>
      <div class="w-0.5 flex-shrink-0 bg-slate-400"></div>
      <div class="w-full flex flex-col gap-1">
        <BlTextBody2 text="Total credits:" class="text-slate-700" />
        <div class="font-bold text-2xl">{{ creditPackage.total_credits }}</div>
      </div>
      <div class="w-0.5 flex-shrink-0 bg-slate-400"></div>
      <div class="w-full flex flex-col gap-1">
        <BlTextBody2 text="Total price:" class="text-slate-700" />
        <div class="flex gap-1">
          <BlTextSubtitle2
            :text="currentCenter.currency_sym"
            class="text-slate-600"
          />
          <div class="font-bold text-2xl">
            {{ priceFormats(creditPackage.total_price) }}
          </div>
        </div>
      </div>
    </div>

    <div class="py-6 px-6 flex flex-col gap-4 bg-pure-white rounded-2xl">
      <BlTextTitle4 text="Item details" />
      <div
        v-for="(service, idx) in creditPackage.package_services"
        :key="service.id"
        class="flex flex-col gap-4"
      >
        <div v-if="idx > 0" class="w-full h-px bg-slate-400"></div>
        <div class="flex flex-col gap-2">
          <router-link
            :to="`/center/package-item`"
            target="_blank"
            class="flex items-center gap-2"
          >
            <div class="font-semibold text-slate-1000 underline">
              {{ service.package_item.name }}
            </div>
            <BlIcon icon="square-up" color="slate-8" class="h-4 w-4" />
            <BlChip
              v-if="service.package_item.status === 'archived'"
              chip="Archived"
              color="rose"
              dense
            />
          </router-link>
          <div class="flex items-center justify-between">
            <BlTextBody2 text="Total credits" class="text-slate-700" />
            <BlTextBody2 :text="service.session_credit" />
          </div>
          <div class="flex items-center justify-between">
            <BlTextBody2 text="Standard retail price" class="text-slate-700" />
            <div>
              <BlTextBody2 :text="currentCenter.currency_sym" />
              <BlTextBody2
                :text="priceFormats(service.standard_retail_price)"
              />
            </div>
          </div>
          <div class="flex items-center justify-between">
            <BlTextBody2 text="Total price" class="text-slate-700" />
            <div>
              <BlTextSubtitle3 :text="currentCenter.currency_sym" />
              <BlTextSubtitle3 :text="priceFormats(service.total_price)" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="py-6 px-6 flex flex-col bg-pure-white rounded-2xl">
      <BlTextTitle4 text="Package details" />
      <div class="py-4 flex gap-4">
        <div class="w-full flex flex-col gap-1">
          <BlTextBody2 text="Expiration days:" class="text-slate-700" />
          <div class="font-bold text-2xl">{{ expirationDate }}</div>
        </div>
        <div class="w-0.5 flex-shrink-0 bg-slate-400"></div>
        <div class="w-full flex flex-col gap-1">
          <BlTextBody2 text="Maximum reschedules:" class="text-slate-700" />
          <div class="font-bold text-2xl">{{ rescheduleCredit }}</div>
        </div>
      </div>
      <div class="py-4 flex flex-col gap-1">
        <BlTextBody2 text="Package name:" class="text-slate-700" />
        <BlTextBody2 :text="creditPackage.name" />
      </div>
      <div class="h-px w-full bg-slate-400"></div>
      <div class="py-4 flex flex-col gap-1">
        <BlTextBody2 text="Status:" class="text-slate-700" />
        <div class="flex items-center gap-1">
          <BlChip
            :chip="packageChips[creditPackage.status].chip"
            :color="packageChips[creditPackage.status].color"
            dense
          />
          <div v-if="creditPackage.archived_at">
            <BlTextBody2 text="on" />
            <BlTextBody2
              :text="
                displayDate({
                  date: creditPackage.archived_at,
                  format: 'DD MMMM YYYY'
                })
              "
            />
            <BlTextBody2 text="by" />
            <BlTextBody2 v-if="!creditPackage.archiver" text="-" />
            <BlTextBody2
              v-else
              :text="creditPackage.archiver.name || 'deleted user'"
              :class="{ ' italic': !creditPackage.archiver.name }"
            />
          </div>
        </div>
      </div>
      <div class="h-px w-full bg-slate-400"></div>
      <div class="py-4 flex flex-col gap-1">
        <BlTextBody2 text="Package description:" class="text-slate-700" />
        <BlTextBody2
          v-if="!creditPackage.description"
          text="No description"
          class="italic text-slate-600"
        />
        <BlTextBody2
          v-else
          :text="creditPackage.description"
          class="whitespace-pre-line"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { displayDate, displayPrice } from "@/plugins/helper.js";

import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlIcon from "../../BlIcon.vue";
import BlChip from "../../BlChip.vue";
import BlTooltip from "../../BlTooltip.vue";

export default {
  name: "PackageDetails",
  components: {
    BlTextTitle4,
    BlTextBody2,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlIcon,
    BlChip,
    BlTooltip
  },
  data() {
    return {
      packageChips: {
        active: { chip: "Active", color: "lime" },
        archived: { chip: "Archived", color: "rose" }
      }
    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter", "creditPackage"]),
    expirationDate() {
      if (this.creditPackage.expiration_date === null) {
        return "No expiration date";
      }
      return `${this.creditPackage.expiration_date} day(s)`;
    },
    rescheduleCredit() {
      if (this.creditPackage.reschedule_credit === null) {
        return "Unlimited";
      }
      return `${this.creditPackage.reschedule_credit} time(s)`;
    }
  },
  async mounted() {},
  methods: {
    ...mapActions([""]),
    displayDate,
    displayPrice,
    priceFormats(price = 0) {
      const settings = this.currentCenter.currency_settings;
      return this.displayPrice({ settings, price });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/center/package/PackageDetails.vue